import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SearchService } from '@nucleus/ui-base';
import { Audit } from '@nucleus/audit-report-ui';
import { NucleusAuditFilter } from '@nucleus/audit-report-ui';
import { ServiceHandler } from '@nucleus/ui-base';
import { PageResult } from '@nucleus/ui-base';
import { FilterRequest } from '../filter-request';
import { Page, QuerySortDirection } from '@nucleus/ui-base';
import {
  AuditControllerService,
  PaginatedFilterRequestAuditFilter,
  PaginatedFilterRequestAuditTrailFilter,
} from '@nucleus/audit-report-ui';
import { RequestHandler } from '../request-handler';
import { AuditTrailFilter } from './audit-trail-filter';


@Injectable({
  providedIn: 'root'
})
export class AuditService implements SearchService<Audit, NucleusAuditFilter> {

  constructor(
    private api: AuditControllerService,
    private requestHandler: RequestHandler,
  ) { }

  findActionCodes(handler: ServiceHandler<string[]>): void {
    this.requestHandler.handleRequest(this.api.findActionCodes() as unknown as Observable<string[]>, handler, false);
  }

  findEntities(handler: ServiceHandler<string[]>): void {
    this.requestHandler.handleRequest(this.api.findEntities(), handler, false);
  }

  search(handler: ServiceHandler<Audit[]>): void {
    this.requestHandler.handleRequest(this.api.findAll() as unknown as Observable<Audit[]>, handler, false);
  }


  searchPaginated(handler: ServiceHandler<PageResult<Audit>>, pageSize: number, pageNumber: number, softField: string, sortDirection: QuerySortDirection) {
    let request = new FilterRequest<NucleusAuditFilter>(new NucleusAuditFilter());
    request.page.pageSize = pageSize;
    request.page.pageNumber = pageNumber;
    request.page.sortDirection = sortDirection;
    request.page.sortField = softField;
    this.filter(handler, request.filter, request.page);
  }

  filterPaginated(handler: ServiceHandler<PageResult<Audit>>, pageSize: number, pageNumber: number, softField: string, sortDirection: QuerySortDirection, filter: NucleusAuditFilter): void {
    let request = new FilterRequest<NucleusAuditFilter>(filter);
    request.page.pageSize = pageSize;
    request.page.pageNumber = pageNumber;
    request.page.sortDirection = sortDirection;
    request.page.sortField = softField;
    this.filter(handler, request.filter, request.page);
  }

  filter(handler: ServiceHandler<PageResult<Audit>>, filter: NucleusAuditFilter, page?: Page, _clientId?: string): void {
    const request = new FilterRequest<NucleusAuditFilter>(filter);
    if (page) { request.page = page; }
    this.requestHandler.handleRequest(
      this.api.filter(this.toApiFilterRequest(request)) as unknown as Observable<PageResult<Audit>>,
      handler,
      false,
    );
  }

  /** Per-entity trail used by the row-icon dialog. Backend gates with
   *  resourceAccessService.canUserAccessResource so an unauthorised
   *  request 401s before any rows leave the database. */
  filterTrail(handler: ServiceHandler<PageResult<Audit>>, request: FilterRequest<AuditTrailFilter>): void {
    const wire: PaginatedFilterRequestAuditTrailFilter = {
      page: {
        pageSize: request.page?.pageSize,
        pageNumber: request.page?.pageNumber,
        sortField: request.page?.sortField,
        sortDirection: request.page?.sortDirection as unknown as string,
      },
      filter: {
        entityName: request.filter.entityName,
        entityId: request.filter.entityId,
      },
    };
    this.requestHandler.handleRequest(
      this.api.filterTrail(wire) as unknown as Observable<PageResult<Audit>>,
      handler,
      false,
    );
  }

  private toApiFilterRequest(request: FilterRequest<NucleusAuditFilter>): PaginatedFilterRequestAuditFilter {
    return {
      page: {
        pageSize: request.page?.pageSize,
        pageNumber: request.page?.pageNumber,
        sortField: request.page?.sortField,
        sortDirection: request.page?.sortDirection as unknown as string,
      },
      filter: { ...(request.filter as any) },
    };
  }

  getPDFReport(handler: ServiceHandler<Blob>, request: FilterRequest<NucleusAuditFilter>) {
    this.requestHandler.handleRequest(this.api.createPdfReport(this.toApiFilterRequest(request)), handler, true);
  }

  getXLSReport(handler: ServiceHandler<Blob>, request: FilterRequest<NucleusAuditFilter>) {
    this.requestHandler.handleRequest(this.api.createExcelReport(this.toApiFilterRequest(request)), handler, true);
  }

  getCSVReport(handler: ServiceHandler<Blob>, request: FilterRequest<NucleusAuditFilter>) {
    this.requestHandler.handleRequest(this.api.createCSVReport(this.toApiFilterRequest(request)), handler, true);
  }
}
